import { Component, Input, OnInit, ElementRef, OnChanges, SimpleChanges, HostListener, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { NavRoute } from '../nav.route.interface';
import { NavigationService, NavDividerCharacter } from '../navigation.service';
import { SecurityService } from '../../../core/security/security.service';
import { UserJsVm } from '../../generated/Admin/Models/User/UserJsVm';
import { GlobalService } from './../../service/global.service';
import { NotificationService } from '../../../services/notification.service';

/**
 * This component is intended to be used in the top header bar
 * to create a nested navigation. Dropdowns are opened by
 * click.
 */
@Component({
    selector: 's2-top-click-nav'
    , templateUrl: './top-click-nav.component.html'
    , styleUrls: ['./top-click-nav.component.scss']
    , standalone: false
})
export class TopClickNavComponent implements OnInit, OnChanges {

    router = inject(Router);
    ns = inject(NotificationService);
    public navService = inject(NavigationService);
    public elRef = inject(ElementRef);
    public sec = inject(SecurityService);

	@Input() navRoutes: NavRoute[] = [];
	@Input() menuItemId = 'root';
	@Input() isRoot = true;

	// Current user information
	user!: UserJsVm;
	// Whether or not the curren nav menu is open
	isOpen!: boolean;
	// Currently OPEN nav menu
	currNavMenuItem!: string;
	// Currently SELECTED nav menu
	currSelectedNavMenuItem!: string;
	// The currently OPEN secondary navigation menu
	currOpenSecondaryNavMenu!: string;
	// Force select item in the nav
	currForceSelect!: string;
	// Classes in the current ul of the nav menu
	ulClasses!: string[];
	// Nav routes used in loop
	loopNavRoutes: NavRoute[] = [];
	// For user nav
	userNavOpen = false;
	// For enabling and disabling hyperlinks
	isMobile = signal<boolean>(false);

	@HostListener('window:resize') onWindowResize() { this.isMobile.set(GlobalService.setIsMobile(window.innerWidth)); }

	ngOnInit() {
      	this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));

		// Get the current user information
		this.sec.user$.subscribe(user => { this.user = user; });
		// Triggred on update of currently OPEN menu item
		this.navService.currOpenPrimaryNavMenu$.subscribe(c => {
			this.currNavMenuItem = c;
			this.isOpen = this.isSubNav(c, this.menuItemId) || c === this.menuItemId;
			this.userNavOpen = c === ['root', 'user'].join(NavDividerCharacter);
			// The first ul should have classes indicating it is the root.
			// Nested ul elements, those at higher depths, are dropdown menus
			// and need classes indicating that.
			this.ulClasses = this.isRoot
				? ['nav', 'navbar-nav', 'top-nav-bg', this.isOpen ? 'nav-open' : '']
				: ['dropdown-menu', 'top-nav-bg', this.isOpen ? 'nav-open' : ''];
		});
		// Triggered on update of currently SELECTED menu item
		this.navService.currSelectedMainNavMenu$.subscribe(c => { this.currSelectedNavMenuItem = c ? c.id! : 'root'; });
		this.navService.currOpenSecondaryNavMenu$.subscribe(c => { this.currOpenSecondaryNavMenu = c ? c.id! : 'root'; });
		this.navService.currForceSelect$.subscribe(c => {
			this.currForceSelect = c;
			this.loopNavRoutes = this.getNavRoutes();
		});
	}

	isSubNav(
		currNavMenuItem: any
		, menuItemId: any
	) {
		// Get divider counts
		const divCountCurrNav = currNavMenuItem.split(NavDividerCharacter)?.length;
		const divCountMenuItem = menuItemId.split(NavDividerCharacter)?.length;

		// Sub navs and current nav should be open
		return divCountMenuItem < divCountCurrNav && currNavMenuItem.startsWith(this.menuItemId);
	}

	ngOnChanges(changes: SimpleChanges) {
		const chng = changes['navRoutes'];
		const cur = chng.currentValue;
		const prev = chng.previousValue;
		if (cur !== prev) { this.loopNavRoutes = this.getNavRoutes(); }
	}

	getHtmlId(routeId: string) { return routeId.replaceAll(/^[^a-z0-9]+|[^\w:.-]+/gi, ''); }

	openDropdown(childMenuId: string) {
		if (childMenuId === 'user') { childMenuId = ['root', 'user'].join(NavDividerCharacter); }
		this.navService.setCurrOpenNavMenu(childMenuId); 
		return false;
	}

	clickDropdown(
		childMenuId: string
		, routeHref: string = ""
	) {
		if (childMenuId === 'user') { childMenuId = ['root', 'user'].join(NavDividerCharacter); }
		if (this.currNavMenuItem.startsWith(childMenuId)) { this.navService.setCurrOpenNavMenu(this.menuItemId); } 
		else { this.navService.setCurrOpenNavMenu(childMenuId); } // Otherwise, open the submenu		
		if (
			routeHref !== '' 
			&& window.innerWidth > 992
		) { globalThis.location.href = routeHref; }
		return false;
	}

	closeDropdown(childMenuId: string) {
		if (!this.isMobile()) { 
			if (childMenuId === 'user') { childMenuId = ['root', 'user'].join(NavDividerCharacter); }
			this.navService.setCurrOpenNavMenu(this.menuItemId); 		
		}		
		return false;
	}

	clickLink(route: NavRoute) { 
		this.navService.setCurrOpenNavMenu(''); 
		if (route != null) {
			if (route.openNewTab) { window.open(route.path); }
			else { this.router.navigate([route.path], {queryParams: route.queryParams}); }
		}
	}

	getNavRoutes() {
		const routeIndex = this.navService.getCurrRouteIndex(this.navRoutes);
		return (
			this.navRoutes
				?.map((o, i) => ({ ...o, index: i }))
				.filter(route => 
					(route?.children == null || route?.children?.length > 0) 
					&& (!route?.shouldDisplay || route?.shouldDisplay(routeIndex!))
				) ?? []
		);
	}
}
